import { useEffect, useState } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import { useModal } from "../../hooks/use-modal.jsx";
import style from "./CheckoutForm.module.css";
import useStripePaymentMethods from "../../hooks/use-stripe-payment-methods";
import PaymentMethod from "./PaymentMethod";
import RadioGroup from "../UI/RadioGroup";
import RadioStandalone from "../UI/RadioStandalone";
import useAccount from "../../hooks/use-account";
import AddressFacturation from "../Account/AddressFacturation";
import CGVCheckbox from "./CGVCheckbox";
import { IntentType, PaymentMethodType } from "../../utils/constants/stripe";
import { trackMetaEvent } from "../../utils/metaPixel";

const PAYMENT_ELEMENT_OPTIONS = {
  layout: "accordion",
};

async function confirmStripeIntent(
  stripe,
  intentType,
  clientSecret,
  paymentMethod,
) {
  if (paymentMethod.type === PaymentMethodType.CARD) {
    const confirmMethod =
      intentType === IntentType.SETUP
        ? stripe.confirmCardSetup
        : stripe.confirmCardPayment;

    return await confirmMethod(
      clientSecret,
      {
        payment_method: paymentMethod.id,
        return_url: `${
          import.meta.env.VITE_FRONTEND_URL
        }/bailleur/stripe/return`,
      },
      {
        handleActions: false,
      },
    );
  } else if (paymentMethod.type === PaymentMethodType.SEPA_DEBIT) {
    const confirmMethod =
      intentType === IntentType.SETUP
        ? stripe.confirmSepaDebitSetup
        : stripe.confirmSepaDebitPayment;
    return await confirmMethod(clientSecret, {
      payment_method: paymentMethod.id,
    });
  }
}

export default function CheckoutForm({
  intentType,
  intentId,
  clientSecret,
  intentStatus,
  setIntentStatus,
  finishPayment,
  onIntentSuccessful,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("info");
  const [isLoading, setIsLoading] = useState(false);
  const [iframeSrc, setIFrameSrc] = useState("about:blank");
  const [cgv, setCgv] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [IFrameModal, closeIFrameModal, openIFrameModal] = useModal(false);
  const {
    paymentMethods,
    isLoading: isLoadingPaymentMethods,
    defaultPaymentMethod,
  } = useStripePaymentMethods();
  const { hasValidAddress } = useAccount();

  useEffect(() => {
    if (paymentMethods.length > 0 && intentType === IntentType.PAYMENT) {
      if (defaultPaymentMethod) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPaymentMethod(defaultPaymentMethod);
      } else {
        setPaymentMethod(paymentMethods[0]);
      }
    }
  }, [defaultPaymentMethod, intentType, paymentMethods]);

  function intentCallback(intent, error) {
    if (error) {
      setMessage(error.message);
      setMessageType("danger");
      return;
    }
    setIntentStatus(intent.status);
    switch (intent.status) {
      case "succeeded":
        setMessage(
          intentType === IntentType.SETUP
            ? "Moyen de paiement enregistré avec succès."
            : "Paiement réussi.",
        );
        setMessageType("success");
        if (onIntentSuccessful) {
          onIntentSuccessful();
        }
        break;
      case "processing":
        setMessage(
          "Paiement en cours de traitement. " +
            "Vos achats serons disponibles automatiquement lorsque le paiement sera validé. " +
            "Vous pouvez fermer cette fenêtre.",
        );
        setMessageType("success");
        if (onIntentSuccessful) {
          onIntentSuccessful();
        }
        break;
      case "requires_payment_method":
        setMessage("Moyen de paiement refusé, veuillez réessayer.");
        setMessageType("danger");
        break;
      case "payment_failed":
        setMessage("Paiement refusé.");
        setMessageType("danger");
        break;
      default:
        setMessage("Une erreur est survenue.");
        setMessageType("danger");
        break;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (intentType === IntentType.PAYMENT) {
      if (!cgv) {
        setMessage("Veuillez accepter les conditions générales de vente.");
        setMessageType("danger");
        return;
      }
      if (!hasValidAddress) {
        setMessage("Veuillez renseigner une adresse de facturation valide.");
        setMessageType("danger");
        return;
      }
    }

    setIsLoading(true);
    if (intentType === IntentType.SETUP) {
      trackMetaEvent("AddPaymentInfo", `api_${intentId}`);
    }

    let finalPaymentMethod = paymentMethod;
    if (!finalPaymentMethod) {
      const submit = await elements.submit();
      if (submit.error) {
        setMessage(submit.error.message);
        setMessageType("danger");
        setIsLoading(false);
        return;
      }
      const createPaymentMethod = await stripe.createPaymentMethod({
        elements,
      });
      if (createPaymentMethod.error) {
        setMessage(createPaymentMethod.error.message);
        setMessageType("danger");
        setIsLoading(false);
        return;
      }
      finalPaymentMethod = createPaymentMethod.paymentMethod;
    }
    const confirmIntent = await confirmStripeIntent(
      stripe,
      intentType,
      clientSecret,
      finalPaymentMethod,
    );
    const intent =
      intentType === IntentType.SETUP
        ? confirmIntent.setupIntent
        : confirmIntent.paymentIntent;
    if (confirmIntent.error) {
      setMessage(confirmIntent.error.message);
      setMessageType("danger");
      setIsLoading(false);
      return;
    }

    if (
      finalPaymentMethod.type === PaymentMethodType.CARD &&
      intent.status === "requires_action"
    ) {
      openIFrameModal();
      setIFrameSrc(intent.next_action.redirect_to_url.url);
      window.stripeCallback = (intent, error) => {
        closeIFrameModal();
        intentCallback(intent, error);
      };
    } else {
      intentCallback(intent, confirmIntent.error);
    }
    setIsLoading(false);
  }

  return (
    <>
      <IFrameModal noPadding>
        <iframe
          title="3D Secure authentification"
          src={iframeSrc}
          width={400}
          height={600}
          className={style.iframe}
        />
      </IFrameModal>
      <form onSubmit={handleSubmit}>
        {intentType === IntentType.PAYMENT && (
          <RadioGroup
            styling={"button"}
            layout={"column"}
            className={style.paymentMethod}
          >
            {paymentMethods.map((method, i) => (
              <RadioStandalone
                name="payment_method"
                checked={method.id === paymentMethod?.id}
                onChange={() => {
                  setPaymentMethod(method);
                }}
                key={i}
                label={<PaymentMethod method={method} flat />}
              />
            ))}
            <RadioStandalone
              name="payment_method"
              label="Nouveau moyen de paiement"
              checked={!paymentMethod}
              onChange={() => setPaymentMethod(null)}
            />
          </RadioGroup>
        )}
        <div
          hidden={
            intentType === IntentType.PAYMENT &&
            (isLoadingPaymentMethods ||
              (paymentMethods.length > 0 && !!paymentMethod))
          }
          className="marginT10"
        >
          <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />
        </div>

        {intentType === IntentType.PAYMENT && (
          <>
            <AddressFacturation />
            <CGVCheckbox
              checked={cgv}
              onChange={() => setCgv((prevState) => !prevState)}
            />
          </>
        )}
        <Callout variant={messageType}>{message}</Callout>
        <div className={"text-right"}>
          {intentStatus === "succeeded" || intentStatus === "processing" ? (
            <Button
              type="button"
              onClick={finishPayment}
              className={"marginT10"}
            >
              Fermer
            </Button>
          ) : (
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading || !stripe || !elements}
              className={"marginT10"}
            >
              {intentType === "setup" ? "Enregistrer" : "Payer"}
            </Button>
          )}
        </div>
      </form>
    </>
  );
}
